import * as R from 'ramda'
import {
  FetchRoomById,
  reqFetchRoomById,
  reqSyncRoom,
  Room,
  RoomTypes,
  SyncRoom,
} from '@weblite/ts-action/room'
import { Message } from '@weblite/ts-action/message'
import { CallType } from '@weblite/ts-action/media-proxy'
import { Mutex } from 'async-mutex'
import { request, requestNow } from '$setup/ws'
import * as seenMentionSync from '$logic/mention/offline-sync'
import { isMessageFromClientUser } from '$logic/message'
import { setReceived } from '$logic/room-seen'
import { unPinRooms } from '$logic/user-preferences'
import { getOpenedRoomId, syncMessagesOfOpenedRoom } from '$logic/opened-room'
import {
  decodeSubscriptionPermissions,
  fetchSubscriptionsDuringSyncRooms,
  leaveHall,
  markSubscriptionAsRead,
  setSubscriptionUnreadMark,
} from '$logic/subscription'
import { SubscriptionPermissions } from '$logic/types/subscription'
import {
  updateRoomLastMessageDebounced,
  updateRoomLastMessages,
} from '$logic/room-last-message'
import { getHall, removeHall } from '$logic/hall'
import {
  deletePv,
  fetchPvsDuringSyncRoom,
  getPv,
  markPvAsRead,
  setPvUnreadMark,
} from '$logic/pv'
import { getUser, getUserDisplayName } from '$logic/users'
import { callService, callStorage } from '$logic/call'
import {
  deleteWappRoom,
  fetchWappRoomsDuringSyncRooms,
  getWappRoom,
  markWappRoomAsRead,
  setWappRoomUnreadMark,
} from '$logic/wapp-room'
import { getWapp } from '$logic/wapps/service'
import { t } from '$i18n'
import {
  hidePrivateSpace,
  markPrivateSpaceAsRead,
  setPrivateSpaceUnreadMark,
} from '$logic/private-space'
import { hallsStorage } from '$logic/hall/storage'
import { CallWappPanel } from '$logic/wapp-panels'
import {
  removeAdministeredChannel,
  setAdministeredChannelsByIds,
} from '$logic/administered-channels'
import * as deletedRoomSync from './deleted-room.offline-sync'
import * as roomSeenSync from '../room-seen/offline-sync'
import {
  nonClientRoomsStorage,
  roomsStorage,
  roomSyncStatusStorage,
} from './storage'
import { subscriptionsStorage } from '../subscription/storage'
import { pvsStorage } from '../pv/storage'
import { privateSpaceStorage } from '../private-space/storage'
import { wappRoomsStorage } from '../wapp-room/storage'
import { syncClientUserPromise } from '$logic/client-user'
import { updateAppUserWaitReporter } from '$logic/auth/events'
import { dateWithOffset } from '$logic/client-device/time-offset.service'

export async function fetchRoom(roomId: string): Promise<Room | undefined> {
  const res = await request<FetchRoomById>(reqFetchRoomById({ roomId }))
  return res.payload?.room
}

export const getRoomFromRedux = roomsStorage.getFromRedux

async function getNonClientRoom(roomId: string) {
  const nonClientRoom = nonClientRoomsStorage.get(roomId)
  if (nonClientRoom) return nonClientRoom
  const fetchedRoom = await fetchRoom(roomId)
  if (fetchedRoom) nonClientRoomsStorage.add(fetchedRoom)
  return fetchedRoom
}

export async function getRoom(roomId: string) {
  if (!roomId) return undefined
  const clientRoom = await roomsStorage.get(roomId)
  if (clientRoom) return clientRoom
  return getNonClientRoom(roomId)
}

const mutexes: Record<string, Mutex> = {}

export function getMutex(id: string) {
  if (mutexes[id]) return mutexes[id]
  mutexes[id] = new Mutex()
  return mutexes[id]
}

export async function updateRoom(
  roomId: string,
  fields: Partial<Room>,
  cb?: () => void,
) {
  await getMutex(roomId).runExclusive(async () => {
    const iidIsInFields = 'iid' in fields
    const clientRoom = await roomsStorage.get(roomId)

    if (clientRoom) {
      const iidShouldUpdate = iidIsInFields && fields.iid! > clientRoom.iid

      await roomsStorage.set(roomId, {
        ...fields,
        iid: iidShouldUpdate ? fields.iid : clientRoom.iid,
      })
      if (iidShouldUpdate) updateRoomLastMessageDebounced(roomId, cb)

      return
    }

    const nonClientRoom = await getNonClientRoom(roomId)

    if (nonClientRoom) {
      return nonClientRoomsStorage.update(roomId, {
        ...fields,
        iid: iidIsInFields
          ? Math.max(nonClientRoom.iid, fields.iid!)
          : nonClientRoom.iid,
      })
    }
  })
}

export const getRooms = roomsStorage.getAll
export const addToDeletedRooms = deletedRoomSync.set

/**
 * NOTE: Use this method only after ensuring that all rooms have been retrieved from the server.
 */
export function isClientsRoom(roomId: string) {
  return !!getRoomFromRedux(roomId)
}

export async function addRoom(room: Room) {
  await getMutex(room.id).runExclusive(async () => {
    const currentClientRoom = await roomsStorage.get(room.id)
    const roomPreviousValue =
      currentClientRoom || nonClientRoomsStorage.get(room.id)

    await roomsStorage.add({
      ...room,
      iid: Math.max(roomPreviousValue?.iid || 0, room.iid), // NOTE: This check is added to prevent some race scenarios
    })

    // in some scenarios before add-new-room event been received, system message with content of 'joining-group' is received and causes race.
    setReceived([{ roomId: room.id, receivedIid: room.iid }])

    /**
     * In some cases, currentClientRoom is null, and the iid in nonClientRoomsStorage matches room.iid.
     * In this situation, we need to ensure that the room’s last message is updated.
     */
    const clientRoomIidShouldUpdate = room.iid > (currentClientRoom?.iid || 0)
    if (clientRoomIidShouldUpdate) updateRoomLastMessageDebounced(room.id)
    return
  })
}

type StorageByRoomType = {
  WAPP_ROOM: typeof wappRoomsStorage
  PV: typeof pvsStorage
  CHANNEL: typeof subscriptionsStorage
  GROUP: typeof subscriptionsStorage
  PRIVATE_SPACE: typeof privateSpaceStorage
}

export function getStorageByRoomTypeAndRoomId<T extends RoomTypes>(
  roomId: string,
  roomType: T,
): StorageByRoomType[T] {
  if (isClientsRoom(roomId)) return storageByRoomType(roomType)
  return {
    get(_id: string): T {},
    async set(fields: Partial<T>): Promise<void> {},
    async del(): Promise<void> {},
  }
}

export function storageByRoomType<T extends RoomTypes>(
  type: T,
): StorageByRoomType[T] {
  switch (type) {
    case 'CHANNEL':
    case 'GROUP':
      return subscriptionsStorage as any
    case 'PV':
      return pvsStorage as any
    case 'PRIVATE_SPACE':
      return privateSpaceStorage as any
    case 'WAPP_ROOM':
      return wappRoomsStorage as any
    default:
      // eslint-disable-next-line no-console
      console.error('Invalid storage for type=', type)
      return null
  }
}

export function isHall(type: RoomTypes): boolean {
  return type === 'CHANNEL' || type === 'GROUP'
}

export async function pruneRoom(roomId: string): Promise<void> {
  const room = await getRoom(roomId)
  if (!room) return

  // TODO #optimize concurrency
  await roomsStorage.del(room.id)

  const { del } = storageByRoomType(room.type)
  await del(roomId)

  await roomSeenSync.del(roomId)
  await seenMentionSync.del(roomId)

  await unPinRooms([roomId])
  if (isHall(room.type)) {
    await hallsStorage.del(roomId)

    if (room.type === 'CHANNEL') await removeAdministeredChannel(room.id)
    //  TODO: syncDeletedPvs hall
  }
  //   TODO: syncDeletedPvs sync data
  //   TODO: syncDeletedPvs messages, pins and ...
}

export const DELETED_MARKER_WIDTH = 30

function calculateDeletedMessageMarkerBasedOnDeletedIids(
  room: Room,
  deletedIids: number[],
) {
  const { iid: lastIid } = room
  const relatedIids = deletedIids.filter(
    iid => lastIid - iid < DELETED_MARKER_WIDTH,
  )
  if (!relatedIids.length) return room.deletedMessageMarker

  // Calculate the binary form. Example: [2, 0] => b101 = 5 (2 and 0 are `lastIid - iid`)
  const binaryIids = R.sum(relatedIids.map(iid => 2 ** (lastIid - iid)))

  // eslint-disable-next-line no-bitwise
  return room.deletedMessageMarker | binaryIids
}

function calculateDeletedMessageMarkerBasedOnNewMessages(
  room: Room,
  messages: Message[],
) {
  // New messages appear at low bits. So for every new message, deletedMessageMarker is shifted once to the left.
  // eslint-disable-next-line no-bitwise
  return room.deletedMessageMarker << messages.length
}

/**
 * when delete patches are received
 * - update deletedMessageMarker
 */
export async function handleDeletedMessages(
  roomId: string,
  iids: number[],
): Promise<void> {
  const room = await getRoom(roomId)
  if (!room) return

  const newDeletedMessageMarker =
    calculateDeletedMessageMarkerBasedOnDeletedIids(room, iids)
  if (newDeletedMessageMarker !== room.deletedMessageMarker)
    await updateRoom(roomId, {
      deletedMessageMarker: newDeletedMessageMarker,
    })
}

/**
 * when new message receives
 * - lastMessage in room + iid
 * - update seen
 * - update deletedMessageMarker
 */
export async function handleNewMessages(messages: Message[]) {
  const grouped = R.groupBy(R.prop('roomId'), messages)
  await Promise.all(
    R.toPairs(grouped).map(async ([roomId, roomMessages]) => {
      const sortedMessages = R.sortBy(R.prop('iid'), roomMessages)

      const notDeletedSortedMessages = sortedMessages.filter(
        msg => !msg.patches?.deleted,
      )

      const {
        roomId: ongoingCallRoomId,
        pvMeta: { roomId: pvCallRoomId },
      } = callStorage.get()

      // End ongoing call if there is a new call message for pv and event message for others
      if (roomId === ongoingCallRoomId || roomId === pvCallRoomId)
        notDeletedSortedMessages.forEach(message => {
          if (
            message.type === 'EVENT' &&
            message.attachment.event === 'CALL_ENDED'
          ) {
            callService.endOngoingHallCall(
              message.attachment.callType as CallType,
            )

            if (
              message.attachment.callType === 'live' ||
              message.attachment.callType === 'classroom'
            )
              CallWappPanel.sendParticipantsCountToWapp(
                message.attachment.participantsCount || 0,
              )
          } else if (message.type === 'CALL') {
            const { duration, status } = message.attachment
            callService.endOngoingPvCall(duration, status)
          }
        })

      const lastMessage = R.last(notDeletedSortedMessages)
      if (!lastMessage) return

      const room = await getRoom(roomId)

      if (room.iid <= lastMessage.iid) {
        await updateRoom(
          roomId,
          {
            iid: lastMessage.iid,
          },
          () =>
            setReceived([
              {
                roomId,
                receivedIid: R.last(sortedMessages)!.iid,
              },
            ]),
        )
      }

      const newDeletedMessageMarker =
        calculateDeletedMessageMarkerBasedOnNewMessages(room, roomMessages)
      if (newDeletedMessageMarker !== room.deletedMessageMarker)
        await updateRoom(roomId, {
          deletedMessageMarker: newDeletedMessageMarker,
        })

      if (room.type === 'PRIVATE_SPACE') return

      const lastSentByClientUser = R.findLast(
        isMessageFromClientUser,
        sortedMessages,
      )
      if (!lastSentByClientUser) return

      const storage = storageByRoomType(room.type)
      const data = await storage.get(roomId)
      if (data.seen < lastSentByClientUser.iid)
        await storage.set(roomId, { seen: lastSentByClientUser.iid })
    }),
  )
}

let roomsAreSynced = false

export function setRoomsAreSynced(val: boolean) {
  roomsAreSynced = val
}

const MAP_ROOM_TYPE_TO_FETCH_FUNCTION_DURING_SYNC_ROOMS: Record<
  Room['type'],
  (roomId: string[]) => Promise<void>
> = {
  WAPP_ROOM: fetchWappRoomsDuringSyncRooms,
  CHANNEL: fetchSubscriptionsDuringSyncRooms,
  GROUP: fetchSubscriptionsDuringSyncRooms,
  PV: fetchPvsDuringSyncRoom,
  PRIVATE_SPACE: Promise.resolve,
}

export async function syncRooms(): Promise<void> {
  updateAppUserWaitReporter.addTimeoutData({
    [`${dateWithOffset()}-start-sync-room`]: true,
  })

  const rooms = getRooms() || []
  const updatedAt = R.reduce<number, number>(
    R.max,
    0,
    rooms.map(R.prop('updatedAt')),
  )
  const { items, del, release } = await deletedRoomSync.getAll()

  updateAppUserWaitReporter.addTimeoutData({
    [`${dateWithOffset()}-got-deleted-room-sync`]: true,
  })

  try {
    const res = await requestNow<SyncRoom>(
      reqSyncRoom({
        deletedRooms: items.map(item => ({ roomId: item.id, ...item.patch })),
        updatedAt,
      }),
    )
    updateAppUserWaitReporter.addTimeoutData({
      [`${dateWithOffset()}-requested-sync-room`]: true,
      thereIsPayload: !!res.payload,
    })

    if (!res.payload) return

    const { roomIds, updatedRooms, adminHallIds } = res.payload
    const deletedRoomIds = R.difference(R.pluck('id', rooms), roomIds)

    const newMessageRooms = updatedRooms.filter(
      ({ id, iid }) => !(iid <= rooms.find(room => room.id === id)?.iid),
    )

    await updateRoomLastMessages(newMessageRooms)

    updateAppUserWaitReporter.addTimeoutData({
      [`${dateWithOffset()}-update-last-messages-done`]: true,
    })

    await Promise.all(deletedRoomIds.map(pruneRoom))

    updateAppUserWaitReporter.addTimeoutData({
      [`${dateWithOffset()}-prune-deleted-rooms-done`]: true,
    })

    await roomsStorage.add(updatedRooms)


    updateAppUserWaitReporter.addTimeoutData({
      [`${dateWithOffset()}-add-updated-rooms-to-room-storage-done`]: true,
    })

    if (roomsAreSynced) {
      const grouped = R.groupBy(R.prop('type'), updatedRooms)
      await Promise.all(
        R.toPairs(grouped).map(async ([type, groupedRoomIds]) => {
          await MAP_ROOM_TYPE_TO_FETCH_FUNCTION_DURING_SYNC_ROOMS[type](
            groupedRoomIds.map(R.prop('id')),
          )
        }),
      )
    }

    updateAppUserWaitReporter.addTimeoutData({
      [`${dateWithOffset()}-synced-updated-rooms`]: true,
    })

    roomSyncStatusStorage.set({ atLeastOnce: true })

    await setAdministeredChannelsByIds(adminHallIds)

    updateAppUserWaitReporter.addTimeoutData({
      [`${dateWithOffset()}-set-administered-channels-by-ids-done`]: true,
    })

    await del()

    updateAppUserWaitReporter.addTimeoutData({
      [`${dateWithOffset()}-delete-sync`]: true,
    })

    // TODO fix: will not `setReceived` next time if app is closed on this line
    syncClientUserPromise.wait().then(() => {
      setReceived(
        newMessageRooms.map(({ id, iid }) => ({
          roomId: id,
          receivedIid: iid,
        })),
      )
    })

    const openedRoomChanges = {
      before: rooms.find(R.propEq('id', getOpenedRoomId())),
      after: newMessageRooms.find(R.propEq('id', getOpenedRoomId())),
    }

    if (openedRoomChanges.after && openedRoomChanges.before)
      syncMessagesOfOpenedRoom(
        openedRoomChanges.after.iid,
        openedRoomChanges.before.iid,
      )
  } finally {
    release()
  }
  setRoomsAreSynced(true)
}

export type RoomPermission = Pick<
  SubscriptionPermissions,
  | 'sendMessage'
  | 'sendWapp'
  | 'sendMedia'
  | 'editMessage'
  | 'deleteMessages'
  | 'pinMessage'
>

export async function getRoomPermissions(
  roomId: string,
): Promise<RoomPermission> {
  const room = await getRoom(roomId)
  if (!room)
    return {
      deleteMessages: false,
      editMessage: false,
      sendMedia: false,
      sendMessage: false,
      sendWapp: false,
      pinMessage: false,
    }

  if (room.type === 'GROUP' || room.type === 'CHANNEL')
    return decodeSubscriptionPermissions(room.id)

  if (room.type === 'WAPP_ROOM')
    return {
      deleteMessages: false,
      editMessage: false,
      sendMedia: false,
      sendMessage: false,
      sendWapp: false,
      pinMessage: false,
    }

  if (room.type === 'PV')
    return {
      deleteMessages: false,
      editMessage: false,
      sendMedia: true,
      sendMessage: true,
      sendWapp: true,
      pinMessage: true,
    }

  // NOTE: room.type is 'PRIVATE_SPACE'
  return {
    deleteMessages: true,
    editMessage: true,
    sendMedia: true,
    sendMessage: true,
    sendWapp: true,
    pinMessage: true,
  }
}

export async function getRoomDisplayName(roomId: string) {
  const room = await getRoom(roomId)
  if (!roomId || !room) return ''
  if (room.type === 'CHANNEL' || room.type === 'GROUP')
    return (await getHall(roomId)).name
  if (room.type === 'PV')
    return getUserDisplayName(await getUser((await getPv(roomId)).to))
  if (room.type === 'WAPP_ROOM')
    return (await getWapp((await getWappRoom(roomId)).wappId)).name
  if (room.type === 'PRIVATE_SPACE') return t('chatList.privateSpace')
  return ''
}

export async function deleteRoomByRoomId(roomId: string) {
  const room = await getRoom(roomId)

  if (room.type === 'PV') {
    return deletePv(roomId, false, true)
  }
  if (room.type === 'WAPP_ROOM') {
    return deleteWappRoom(roomId, false, true)
  }
  if (room.type === 'PRIVATE_SPACE') {
    return hidePrivateSpace(false, true)
  }
  const permissions = await decodeSubscriptionPermissions(roomId)
  if (permissions.isOwner) return removeHall(roomId, false, true)
  return leaveHall(roomId, true)
}

export async function markRoomAsRead(roomId: string) {
  analite('CHATLIST_MARK_UNREED', { active: true })
  const room = await getRoom(roomId)
  if (room.type === 'PV') await markPvAsRead(room.id)
  else if (room.type === 'WAPP_ROOM') await markWappRoomAsRead(room.id)
  else if (room.type === 'PRIVATE_SPACE') await markPrivateSpaceAsRead()
  else await markSubscriptionAsRead(room.id)
}

export async function markRoomAsUnread(roomId: string) {
  analite('CHATLIST_MARK_UNREED', { active: false })
  const room = await getRoom(roomId)
  if (room.type === 'PV') await setPvUnreadMark(room.id, true)
  else if (room.type === 'WAPP_ROOM') await setWappRoomUnreadMark(room.id, true)
  else if (room.type === 'PRIVATE_SPACE') await setPrivateSpaceUnreadMark(true)
  else await setSubscriptionUnreadMark(room.id, true)
}
